import React from 'react';
import { Box, BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem, styled } from '@withjoy/joykit/theme';
import { Skeleton } from '@shared/components/Skeleton';
import { shimmerMixin } from '@shared/components/Skeleton/Skeleton.styles';
import { useTranslation } from '@shared/core';

const StyledSkeleton = styled(Box)`
  ${shimmerMixin}
`;

interface HotelSkeletonProps {
  id: string;
}

export const HotelSkeleton = ({ id }: HotelSkeletonProps) => {
  const { t } = useTranslation('websiteEditor');
  const hotelsTrans = t('venueHotels');
  return (
    <Flex
      alignItems={'center'}
      gap={5}
      width={'100%'}
      flexDirection={{ __: 'column', xs: 'row' }}
    >
      <TextV2
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        typographyVariant={'hed1'}
        fontSize={pxToRem(16)}
        fontWeight={600}
        borderRadius={'50%'}
        border={'1px solid'}
        borderColor={'mono3'}
        width={pxToRem(40)}
        height={pxToRem(40)}
        textAlign={'center'}
        flex={'none'}
        marginBottom={{ __: 0, xs: pxToRem(53) }}
      >
        {id}
      </TextV2>
      <Flex
        width={'100%'}
        gap={7}
        flexDirection={'column'}
      >
        <Flex
          gap={5}
          padding={5}
          border={'1px solid'}
          borderColor={'mono3'}
          borderRadius={pxToRem(16)}
          alignItems={'center'}
          flex={1}
          position={'relative'}
          flexDirection={{ __: 'column', md: 'row' }}
          background={'linear-gradient(93deg, #ECECEC 0.57%, #F7F7F7 99.16%)'}
          width={'100%'}
        >
          <Skeleton
            width={{ __: '100%', md: pxToRem(122) }}
            height={pxToRem(122)}
            borderRadius={pxToRem(8)}
            display={'flex'}
            flex={'none'}
            flexShrink={0}
            backgroundColor={'rgba(255, 255, 255, 0.80)'}
            placeholder={
              <Box
                width={{ __: '100%', md: pxToRem(122) }}
                height={pxToRem(122)}
                borderRadius={pxToRem(8)}
                display={'flex'}
                flex={'none'}
                flexShrink={0}
                backgroundColor={'rgba(255, 255, 255, 0.80)'}
              >
                &nbsp;
              </Box>
            }
          />
          <Flex
            flexDirection={'column'}
            width={'100%'}
            justifyContent={'space-between'}
            height={pxToRem(122)}
          >
            <Flex
              width={'100%'}
              flexDirection={'column'}
            >
              <BlankSkeleton
                typographyVariant={'hed1'}
                width={'100%'}
              />
              <Flex
                marginTop={3}
                gap={4}
                alignItems={'center'}
              >
                <BlankSkeleton
                  typographyVariant={'body1'}
                  width={'20%'}
                />
                <BlankSkeleton
                  typographyVariant={'body1'}
                  width={'30%'}
                />
                <BlankSkeleton
                  typographyVariant={'body1'}
                  width={'20%'}
                />
              </Flex>
            </Flex>
            <Flex
              justifyContent={'space-between'}
              gap={3}
              width={'100%'}
            >
              <Flex
                width={'50%'}
                flexDirection={'column'}
                gap={3}
                flex={1}
                alignItems={'flex-start'}
                justifyContent="center"
              >
                <BlankSkeleton
                  typographyVariant={'label2'}
                  width={'80%'}
                />
                <Flex
                  gap={pxToRem(7)}
                  alignItems={'center'}
                  width={'100%'}
                >
                  <Skeleton
                    width={pxToRem(24)}
                    height={pxToRem(24)}
                    borderRadius={'50%'}
                    placeholder={
                      <Box
                        background={'rgba(255, 255, 255, 0.8)'}
                        width={pxToRem(24)}
                        height={pxToRem(24)}
                        borderRadius={'50%'}
                      >
                        &nbsp;
                      </Box>
                    }
                  />
                  <BlankSkeleton
                    typographyVariant={'label2'}
                    width={'40%'}
                    fontWeight={400}
                  />
                </Flex>
              </Flex>
              <Flex
                width={'50%'}
                flexDirection={'column'}
                gap={1}
                flexWrap={'wrap-reverse'}
                justifyContent={'flex-end'}
              >
                <Skeleton
                  typographyVariant="label2"
                  fontWeight={500}
                  fontSize={pxToRem(13)}
                  lineHeight={'18px'}
                  borderRadius={200}
                  backgroundColor={'accentGreen2'}
                  color={'accentGreen3'}
                  width={'40%'}
                  whiteSpace={'nowrap'}
                  placeholder={
                    <Box
                      background={'rgba(255, 255, 255, 0.8)'}
                      width="100%"
                      borderRadius={200}
                    >
                      &nbsp;
                    </Box>
                  }
                />
                <Flex
                  gap={3}
                  justifyContent={'flex-end'}
                >
                  <BlankSkeleton
                    typographyVariant={'label2'}
                    fontWeight={400}
                    color={'mono8'}
                    width={'40%'}
                  />
                  <BlankSkeleton
                    typographyVariant={'label2'}
                    fontWeight={400}
                    color={'mono8'}
                    width={'40%'}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <StyledSkeleton
          background="linear-gradient(90deg, #BDBDBD 0%, #F1F1F1 100%)"
          backgroundColor={'rgb(0, 0, 0, 0.2) !important'}
          css={{
            'background-clip': 'text',
            '-webkit-background-clip': 'text',
            '-webkit-text-fill-color': 'rgb(0, 0, 0, 0.2)',
          }}
        >
          <TextV2 typographyVariant={'hed1'}>{hotelsTrans.reviewingHotels()}</TextV2>
        </StyledSkeleton>
      </Flex>
    </Flex>
  );
};

const BlankSkeleton = (props: BoxProps) => (
  <Skeleton
    {...props}
    placeholder={
      <Box
        backgroundColor={'rgba(255, 255, 255, 0.8)'}
        width="100%"
      >
        &nbsp;
      </Box>
    }
  />
);
